import { useState, useEffect, useCallback } from 'react';

interface ImageLightboxProps {
  images: string[];
  currentIndex: number;
  alt: string;
  onClose: () => void;
  onPrev: () => void;
  onNext: () => void;
}

export default function ImageLightbox({ images, currentIndex, alt, onClose, onPrev, onNext }: ImageLightboxProps) {
  const [zoom, setZoom] = useState(1);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    requestAnimationFrame(() => setIsVisible(true));
  }, []);

  useEffect(() => {
    setZoom(1);
  }, [currentIndex]);

  const handleClose = useCallback(() => {
    setIsVisible(false);
    setTimeout(onClose, 200);
  }, [onClose]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleClose();
      if (e.key === 'ArrowLeft') onPrev();
      if (e.key === 'ArrowRight') onNext();
    };
    window.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [handleClose, onPrev, onNext]);

  const handleZoomIn = () => setZoom((prev) => Math.min(prev + 0.5, 3));
  const handleZoomOut = () => setZoom((prev) => Math.max(prev - 0.5, 0.5));

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) handleClose();
  };

  return (
    <div
      className={`fixed inset-0 z-[100] flex items-center justify-center transition-all duration-200 ${
        isVisible ? 'bg-black/90' : 'bg-black/0'
      }`}
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-label={alt}
    >
      {/* Close Button */}
      <button
        onClick={handleClose}
        className="absolute top-4 right-4 md:top-6 md:right-6 w-10 h-10 flex items-center justify-center rounded-full bg-background-50/10 hover:bg-background-50/20 text-background-50 backdrop-blur-sm transition-all duration-200 z-10 whitespace-nowrap cursor-pointer"
        aria-label="Close lightbox"
      >
        <i className="ri-close-line text-xl"></i>
      </button>

      {/* Image Counter */}
      <div className="absolute top-4 left-4 md:top-6 md:left-6 px-3 py-1.5 rounded-full bg-background-50/10 backdrop-blur-sm text-background-50 text-xs font-medium z-10">
        {currentIndex + 1} / {images.length}
      </div>

      {/* Zoom Controls */}
      <div className="absolute bottom-4 md:bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-2 z-10">
        <button
          onClick={handleZoomOut}
          disabled={zoom <= 0.5}
          className="w-8 h-8 flex items-center justify-center rounded-full bg-background-50/10 hover:bg-background-50/20 text-background-50 backdrop-blur-sm transition-all duration-200 disabled:opacity-30 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
          aria-label="Zoom out"
        >
          <i className="ri-zoom-out-line"></i>
        </button>
        <span className="text-background-50/70 text-xs font-medium min-w-[3rem] text-center">{Math.round(zoom * 100)}%</span>
        <button
          onClick={handleZoomIn}
          disabled={zoom >= 3}
          className="w-8 h-8 flex items-center justify-center rounded-full bg-background-50/10 hover:bg-background-50/20 text-background-50 backdrop-blur-sm transition-all duration-200 disabled:opacity-30 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
          aria-label="Zoom in"
        >
          <i className="ri-zoom-in-line"></i>
        </button>
      </div>

      {/* Previous Arrow */}
      {images.length > 1 && (
        <button
          onClick={(e) => { e.stopPropagation(); onPrev(); }}
          className="absolute left-3 md:left-6 top-1/2 -translate-y-1/2 w-10 h-10 md:w-12 md:h-12 flex items-center justify-center rounded-full bg-background-50/10 hover:bg-background-50/20 text-background-50 backdrop-blur-sm transition-all duration-200 z-10 whitespace-nowrap cursor-pointer"
          aria-label="Previous image"
        >
          <i className="ri-arrow-left-s-line text-xl md:text-2xl"></i>
        </button>
      )}

      {/* Image Container */}
      <div className="relative max-w-[90vw] max-h-[85vh] overflow-hidden">
        <img
          src={images[currentIndex]}
          alt={alt}
          title={alt}
          className="max-w-[90vw] max-h-[85vh] object-contain transition-transform duration-300 ease-out select-none"
          style={{ transform: `scale(${zoom})` }}
          draggable={false}
        />
      </div>

      {/* Next Arrow */}
      {images.length > 1 && (
        <button
          onClick={(e) => { e.stopPropagation(); onNext(); }}
          className="absolute right-3 md:right-6 top-1/2 -translate-y-1/2 w-10 h-10 md:w-12 md:h-12 flex items-center justify-center rounded-full bg-background-50/10 hover:bg-background-50/20 text-background-50 backdrop-blur-sm transition-all duration-200 z-10 whitespace-nowrap cursor-pointer"
          aria-label="Next image"
        >
          <i className="ri-arrow-right-s-line text-xl md:text-2xl"></i>
        </button>
      )}

      {/* Dots Indicator */}
      {images.length > 1 && (
        <div className="absolute bottom-16 md:bottom-20 left-1/2 -translate-x-1/2 flex items-center gap-1.5 z-10">
          {images.map((_, idx) => (
            <button
              key={idx}
              onClick={(e) => { e.stopPropagation(); idx < currentIndex ? onPrev() : idx > currentIndex ? onNext() : null; }}
              className={`w-2 h-2 rounded-full transition-all duration-300 cursor-pointer ${
                idx === currentIndex
                  ? 'bg-background-50 w-6'
                  : 'bg-background-50/40 hover:bg-background-50/70'
              }`}
              aria-label={`Go to image ${idx + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
}